import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Wind, AlertTriangle, Activity, Pill, FileText, CheckCircle, Shield,
  Users, Clock, DollarSign, Clipboard, Heart, Zap, ChevronRight,
  AlertCircle, Phone, Bell, Timer, Home, Stethoscope, Package
} from "lucide-react";

const RespiratoryCrisisPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('assessment');
  const [loading, setLoading] = useState(false);
  const [crisisResult, setCrisisResult] = useState(null);
  const [activeCrises, setActiveCrises] = useState(null);
  const [chcGapAlerts, setChcGapAlerts] = useState([]);
  
  const [assessment, setAssessment] = useState({
    patient_id: '',
    patient_name: '',
    chart_id: '',
    vital_signs: {
      o2_saturation: 92,
      respiratory_rate: 22,
      heart_rate: 88,
      blood_pressure: '120/80',
      temperature: 98.6
    },
    accessory_muscle_use: false,
    cyanosis_present: false,
    terminal_secretions: false,
    anxiety_level: 3,
    work_of_breathing: 'normal',
    lung_sounds: 'clear',
    current_o2_therapy: 'none',
    current_medications: '',
    pain_level: 2,
    terminal_diagnosis: '',
    assessed_by: user?.name || 'RN'
  });

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadActiveCrises();
    loadCHCGapAlerts();
  }, []);

  const loadActiveCrises = async () => {
    try {
      const response = await api.get('/ehr/respiratory-crisis/dashboard/active-crises');
      setActiveCrises(response.data);
    } catch (error) {
      console.error("Failed to load active crises:", error);
    }
  };

  const loadCHCGapAlerts = async () => {
    try {
      const response = await api.get('/ehr/respiratory-crisis/chc/alert/gap');
      setChcGapAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to load CHC gap alerts:", error);
    }
  };

  const handleActivateCrisis = async () => {
    if (!assessment.patient_name) {
      toast.error("Please enter patient name");
      return;
    }

    setLoading(true);
    try {
      const medsArray = assessment.current_medications.split(',').map(m => m.trim()).filter(m => m);
      
      const response = await api.post('/ehr/respiratory-crisis/crisis/activate', {
        ...assessment,
        current_medications: medsArray
      });
      
      setCrisisResult(response.data);
      
      if (response.data.severity === 'critical') {
        toast.error("🚨 CRITICAL RESPIRATORY CRISIS - Immediate intervention required!", { duration: 6000 });
      } else if (response.data.severity === 'severe') {
        toast.warning("Severe respiratory distress detected - See protocol", { duration: 5000 });
      } else {
        toast.success(`Respiratory assessment complete: ${response.data.severity.toUpperCase()}`);
      }
      
      if (response.data.loc_recommendation.includes('CHC') || response.data.loc_recommendation.includes('GIP')) {
        toast.info("Level of Care change recommended - Review options", { duration: 4000 });
      }
    } catch (error) {
      console.error("Crisis activation failed:", error);
      toast.error(error.response?.data?.detail || "Failed to process respiratory assessment");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-300';
      case 'severe': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'moderate': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-green-100 text-green-700 border-green-300';
    }
  };

  const isCrisisThreshold = () => {
    return assessment.vital_signs.o2_saturation < 88 || assessment.vital_signs.respiratory_rate > 28;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Wind className="w-8 h-8 text-cyan-600" />
                Respiratory Crisis Management
              </h1>
              <p className="text-muted-foreground mt-1">
                Crisis Protocol → LOC Transition → Emergency Orders → IDG Alerts
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
              Back to EHR
            </Button>
          </div>
        </div>

        {/* Crisis Alert Banner */}
        {chcGapAlerts.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Bell className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-red-700">CHC Documentation Gap Alert</p>
                {chcGapAlerts.map((alert, idx) => (
                  <div key={idx} className="mt-2">
                    <p className="text-sm text-red-600">{alert.message}</p>
                    <Button size="sm" variant="destructive" className="mt-2">
                      Log CHC Entry Now
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {activeCrises && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Crises</p>
                    <p className="text-2xl font-bold text-red-600">{activeCrises.active_crises}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">On CHC</p>
                    <p className="text-2xl font-bold text-orange-600">{activeCrises.chc_patients}</p>
                  </div>
                  <Clock className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">On GIP</p>
                    <p className="text-2xl font-bold text-purple-600">{activeCrises.gip_patients}</p>
                  </div>
                  <Home className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Doc Gaps</p>
                    <p className="text-2xl font-bold">{chcGapAlerts.length}</p>
                  </div>
                  <FileText className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="assessment" data-testid="tab-assessment">
              <Activity className="w-4 h-4 mr-2" />
              Respiratory Assessment
            </TabsTrigger>
            <TabsTrigger value="protocol" data-testid="tab-protocol">
              <Zap className="w-4 h-4 mr-2" />
              Crisis Protocol
            </TabsTrigger>
            <TabsTrigger value="loc" data-testid="tab-loc">
              <Home className="w-4 h-4 mr-2" />
              LOC Management
            </TabsTrigger>
          </TabsList>

          {/* Respiratory Assessment Tab */}
          <TabsContent value="assessment">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-600" />
                      Respiratory Assessment
                    </CardTitle>
                    <CardDescription>
                      O2 &lt;88% or RR &gt;28 triggers Crisis Management Protocol
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Patient Name *</Label>
                        <Input
                          value={assessment.patient_name}
                          onChange={(e) => setAssessment(prev => ({ ...prev, patient_name: e.target.value }))}
                          placeholder="Enter patient name"
                          data-testid="patient-name-input"
                        />
                      </div>
                      <div>
                        <Label>Terminal Diagnosis</Label>
                        <Input
                          value={assessment.terminal_diagnosis}
                          onChange={(e) => setAssessment(prev => ({ ...prev, terminal_diagnosis: e.target.value }))}
                          placeholder="e.g., Stage IV Lung Cancer"
                        />
                      </div>
                    </div>

                    {/* Vital Signs */}
                    <div className="p-4 border rounded-lg bg-slate-50">
                      <Label className="font-semibold mb-3 block">Vital Signs</Label>
                      <div className="grid grid-cols-5 gap-4">
                        <div>
                          <Label className={`text-sm ${assessment.vital_signs.o2_saturation < 88 ? 'text-red-600 font-bold' : ''}`}>
                            O2 Sat %
                          </Label>
                          <Input
                            type="number"
                            value={assessment.vital_signs.o2_saturation}
                            onChange={(e) => setAssessment(prev => ({
                              ...prev,
                              vital_signs: { ...prev.vital_signs, o2_saturation: parseInt(e.target.value) || 0 }
                            }))}
                            className={assessment.vital_signs.o2_saturation < 88 ? 'border-red-500 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <Label className={`text-sm ${assessment.vital_signs.respiratory_rate > 28 ? 'text-red-600 font-bold' : ''}`}>
                            RR /min
                          </Label>
                          <Input
                            type="number"
                            value={assessment.vital_signs.respiratory_rate}
                            onChange={(e) => setAssessment(prev => ({
                              ...prev,
                              vital_signs: { ...prev.vital_signs, respiratory_rate: parseInt(e.target.value) || 0 }
                            }))}
                            className={assessment.vital_signs.respiratory_rate > 28 ? 'border-red-500 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">HR /min</Label>
                          <Input
                            type="number"
                            value={assessment.vital_signs.heart_rate}
                            onChange={(e) => setAssessment(prev => ({
                              ...prev,
                              vital_signs: { ...prev.vital_signs, heart_rate: parseInt(e.target.value) || 0 }
                            }))}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">BP</Label>
                          <Input
                            value={assessment.vital_signs.blood_pressure}
                            onChange={(e) => setAssessment(prev => ({
                              ...prev,
                              vital_signs: { ...prev.vital_signs, blood_pressure: e.target.value }
                            }))}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Temp °F</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={assessment.vital_signs.temperature}
                            onChange={(e) => setAssessment(prev => ({
                              ...prev,
                              vital_signs: { ...prev.vital_signs, temperature: parseFloat(e.target.value) || 0 }
                            }))}
                          />
                        </div>
                      </div>
                      
                      {isCrisisThreshold() && (
                        <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-300">
                          <p className="text-red-700 font-bold flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            CRISIS THRESHOLD DETECTED
                          </p>
                          <p className="text-sm text-red-600">
                            {assessment.vital_signs.o2_saturation < 88 && `O2 Sat ${assessment.vital_signs.o2_saturation}% < 88% `}
                            {assessment.vital_signs.respiratory_rate > 28 && `RR ${assessment.vital_signs.respiratory_rate} > 28`}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Clinical Signs */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Work of Breathing</Label>
                        <Select 
                          value={assessment.work_of_breathing}
                          onValueChange={(value) => setAssessment(prev => ({ ...prev, work_of_breathing: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="mild">Mild</SelectItem>
                            <SelectItem value="moderate">Moderate</SelectItem>
                            <SelectItem value="severe">Severe</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Current O2 Therapy</Label>
                        <Select 
                          value={assessment.current_o2_therapy}
                          onValueChange={(value) => setAssessment(prev => ({ ...prev, current_o2_therapy: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="2L">2L Nasal Cannula</SelectItem>
                            <SelectItem value="4L">4L Nasal Cannula</SelectItem>
                            <SelectItem value="6L">6L Nasal Cannula</SelectItem>
                            <SelectItem value="high_flow">High Flow</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={assessment.accessory_muscle_use}
                          onCheckedChange={(checked) => setAssessment(prev => ({ ...prev, accessory_muscle_use: checked }))}
                        />
                        <Label className="text-sm">Accessory Muscles</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={assessment.cyanosis_present}
                          onCheckedChange={(checked) => setAssessment(prev => ({ ...prev, cyanosis_present: checked }))}
                        />
                        <Label className="text-sm">Cyanosis</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={assessment.terminal_secretions}
                          onCheckedChange={(checked) => setAssessment(prev => ({ ...prev, terminal_secretions: checked }))}
                        />
                        <Label className="text-sm">Terminal Secretions</Label>
                      </div>
                      <div>
                        <Label className="text-sm">Anxiety (0-10)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          value={assessment.anxiety_level}
                          onChange={(e) => setAssessment(prev => ({ ...prev, anxiety_level: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Current Medications (comma-separated)</Label>
                      <Input
                        value={assessment.current_medications}
                        onChange={(e) => setAssessment(prev => ({ ...prev, current_medications: e.target.value }))}
                        placeholder="e.g., Morphine 10mg q4h, Lorazepam 0.5mg PRN"
                      />
                    </div>

                    <Button 
                      onClick={handleActivateCrisis}
                      disabled={loading}
                      className={`w-full ${isCrisisThreshold() ? 'bg-red-600 hover:bg-red-700' : ''}`}
                      data-testid="activate-crisis-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      {isCrisisThreshold() ? 'ACTIVATE CRISIS PROTOCOL' : 'Process Respiratory Assessment'}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Reference */}
              <div className="space-y-4">
                <Card className="border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-red-700">Crisis Thresholds</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <div className="p-2 bg-red-50 rounded">
                      <p className="font-bold">O2 Saturation &lt; 88%</p>
                      <p className="text-xs text-red-600">Triggers Crisis Protocol</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="font-bold">Respiratory Rate &gt; 28</p>
                      <p className="text-xs text-red-600">Triggers Crisis Protocol</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">LOC Options</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <div className="p-2 bg-orange-50 rounded">
                      <p className="font-medium">CHC (Continuous Home Care)</p>
                      <p className="text-xs">Nursing care needed &gt;8 hours</p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded">
                      <p className="font-medium">GIP (General Inpatient)</p>
                      <p className="text-xs">Requires facility-level care</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">HOPE Section O</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Auto-populated based on assessment:</p>
                    <ul className="mt-2 space-y-1 text-xs">
                      <li>• SOB Severity (0-3)</li>
                      <li>• O2 Therapy Status</li>
                      <li>• SFV 48hr Scheduled</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Crisis Protocol Tab */}
          <TabsContent value="protocol">
            {crisisResult ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Severity & LOC */}
                <Card className={`border-2 ${getSeverityColor(crisisResult.severity)}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Crisis Status</span>
                      <Badge className={getSeverityColor(crisisResult.severity)}>
                        {crisisResult.severity.toUpperCase()}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">LOC Recommendation</p>
                        <p className="text-sm">{crisisResult.loc_recommendation}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="font-medium">HOPE Section O</p>
                        <p className="text-sm">
                          SOB: {crisisResult.hope_section_o?.shortness_of_breath?.description}
                        </p>
                        <p className="text-sm">
                          O2: {crisisResult.hope_section_o?.oxygen_therapy?.description}
                        </p>
                        {crisisResult.sfv_scheduled && (
                          <Badge className="mt-2 bg-green-100 text-green-700">SFV Scheduled</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Medication Orders */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Pill className="w-5 h-5" />
                      Medication Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {crisisResult.medication_orders?.map((order, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <span className="font-medium">{order.medication}</span>
                            <Badge variant="outline">{order.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{order.dose}</p>
                          <p className="text-xs text-blue-600 mt-1">{order.clinical_justification}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* DME Orders */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Emergency DME
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {crisisResult.dme_orders?.map((dme, idx) => (
                        <div key={idx} className="p-3 border rounded-lg bg-cyan-50">
                          <div className="flex justify-between items-start">
                            <span className="font-medium">{dme.name}</span>
                            <Badge className="bg-red-100 text-red-700">{dme.delivery_priority}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{dme.description}</p>
                          <p className="text-xs text-cyan-600 mt-1">ETA: {dme.estimated_delivery}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* IDG Alerts */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      IDG Live Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {crisisResult.idg_alerts?.map((alert, idx) => (
                        <div key={idx} className={`p-3 border rounded-lg ${
                          alert.priority === 'immediate' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={alert.priority === 'immediate' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                              {alert.discipline}
                            </Badge>
                            <span className="text-xs">Response: {alert.required_response}</span>
                          </div>
                          <p className="text-sm">{alert.message}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Family Portal & Timer */}
                <Card>
                  <CardHeader>
                    <CardTitle>Family Communication</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm">{crisisResult.family_portal_message}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Timer className="w-5 h-5" />
                      Crisis-to-Comfort Timer
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-2">Target: {crisisResult.crisis_to_comfort_timer?.target}</p>
                    <div className="space-y-2">
                      {crisisResult.crisis_to_comfort_timer?.checkpoints?.map((cp, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">{cp.time}:</span>
                          <span>{cp.action}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Audit Requirements */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Grade A Audit Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {crisisResult.audit_grade_requirements?.map((req, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{req}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Wind className="w-12 h-12 mx-auto text-muted-foreground opacity-30 mb-2" />
                  <p className="text-muted-foreground">Complete respiratory assessment to see crisis protocol</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* LOC Management Tab */}
          <TabsContent value="loc">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Active LOC Cases</CardTitle>
                </CardHeader>
                <CardContent>
                  {activeCrises?.patients?.map((patient, idx) => (
                    <div key={idx} className={`p-4 mb-3 border rounded-lg ${
                      patient.loc === 'chc' ? 'bg-orange-50 border-orange-200' :
                      patient.loc === 'gip' ? 'bg-purple-50 border-purple-200' :
                      'bg-slate-50'
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold">{patient.patient_name}</span>
                        <Badge className={getSeverityColor(patient.crisis_severity)}>
                          {patient.crisis_severity}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">LOC:</span>
                          <span className="ml-1 font-medium uppercase">{patient.loc}</span>
                        </div>
                        {patient.hours_on_chc && (
                          <div>
                            <span className="text-muted-foreground">Hours:</span>
                            <span className="ml-1 font-medium">{patient.hours_on_chc}h</span>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">O2:</span>
                          <span className="ml-1">{patient.current_o2_sat}%</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">RR:</span>
                          <span className="ml-1">{patient.current_rr}</span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <Badge className={
                          patient.status === 'improving' ? 'bg-green-100 text-green-700' :
                          patient.status === 'declining' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {patient.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">Updated: {patient.last_update}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>CHC Documentation Requirements</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="font-medium text-orange-700">Hourly Documentation</p>
                    <ul className="text-sm text-orange-600 mt-1 space-y-1">
                      <li>• Clinical status update every 60 minutes</li>
                      <li>• Interventions provided</li>
                      <li>• Patient response</li>
                    </ul>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-700">15-Minute Gap Alert</p>
                    <p className="text-sm text-red-600">
                      If 15 minutes pass without an entry, you'll receive: 
                      "CHC Documentation Gap: Update respiratory status and interventions now to ensure billable hours."
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-700">Billing Requirements</p>
                    <ul className="text-sm text-blue-600 mt-1 space-y-1">
                      <li>• Minimum 8 hours to qualify for CHC billing</li>
                      <li>• Must document nursing-level care</li>
                      <li>• Time logs must match narratives</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default RespiratoryCrisisPage;
